import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { TabsetComponent } from 'ng-uikit-pro-standard';
import { BehaviorSubject, combineLatest, fromEvent, merge, Observable, of } from 'rxjs';
import { catchError, debounce, debounceTime, delay, filter, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/share/services/auth.service';
import { CommonService } from 'src/app/share/services/common.service';
import { IUser } from '../share/entities/user';
import { DateUtil } from '../share/utils/date-util';

@Component({
  selector: 'app-app-buddy',
  templateUrl: './app-buddy.component.html',
  styleUrls: ['./app-buddy.component.scss']
})
export class AppBuddyComponent implements OnInit {

  constructor(private activatedRoute: ActivatedRoute, private commonService: CommonService, private authService: AuthService) { }

  vm$: Observable<IViewModel>;

  refresh$: BehaviorSubject<Params> = new BehaviorSubject<Params>(undefined);

  commandVm$: Observable<ICommandViewModel>;

  contentBs$: BehaviorSubject<IContentViewModel> = new BehaviorSubject<IContentViewModel>({ lastUpdated: DateUtil.now() });

  actionBs$: BehaviorSubject<IAction> = new BehaviorSubject<IAction>(undefined);

  heartBeatVm$: Observable<IHeartBeatViewModel>;

  /**
   *
   */
  refreshPage(vm: IViewModel) {
    this.refresh$.next(vm);
  }

  /**
   *
   */
  ngOnInit() {

    // page
    this.vm$ = this.activatedRoute.queryParams.pipe(
      map((params) => {
        const ret: IViewModel = {
          params: params
        };
        return ret;
      })
    );

    // heart beat
    this.heartBeatVm$ = merge(
      of({ detail: { dateTime: undefined } }),
      fromEvent(document, 'client-heartbeat').pipe(
        tap((v: CustomEvent<IHostHeartBeatArg>) => {
          if (!v.detail.dateTime)
            v.detail.dateTime = new Date(Date.now());
        })
      )
    ).pipe(
      debounceTime(1000),
      map((v: CustomEvent<IHostHeartBeatArg>) => {
        return { data: v.detail };
      })
    );

    // command
    this.commandVm$ = merge<ICommandViewModel>(
      of({}),


      fromEvent(document, 'initialize').pipe(
        switchMap((v: CustomEvent<IHostInitializationArg>) => merge(
          of({ workingText: 'Initializing...' }),
          this.handleCommand_initialize(v.detail)
        ))
      ),

      fromEvent(document, 'host-context-changed').pipe(
        switchMap((v: CustomEvent<IHostContextChangedArg>) => merge(
          of({ workingText: 'Host Context Changing...' }),
          this.handleCommand_hostContextChanged(v.detail)
        ))
      ),

      this.actionBs$.pipe(
        filter(a => a != undefined),
        switchMap((action: IAction) => merge(
          of({ workingText: `Action '${action.type}' executing...` }),
          this.handleCommand_action(action)
        ))
      )
    ).pipe(
      catchError(e => {
        return of({ errorText: e });
      })
    );

  }

  /**
   *
   * @param action
   */
  private handleCommand_action(action: IAction): Observable<ICommandViewModel> {
    console.log({
      context: 'action', data: action
    });

    return this.pageCommand<ICommandResultAction>(`res.action.${action.type}`, action.paramData).pipe(
      tap((result: ICommandResult<ICommandResultAction>) => {
        if (result.workingText)
          return;
        if (result.errorText)
          return;

        const contentVm: IContentViewModel = {
          ...this.contentBs$.getValue()
        };

        if (action.type == 'pickup-zone-changed') {
          contentVm.affiliateVm.data.dispatchWorkflowItems = result.data?.resultData?.dispatchWorkflowItems ?? [];
        }

        this.contentBs$.next(contentVm);
      }),
      catchError(e => {
        console.log({
          context: 'handleCommand_action',
          error: e
        });
        return of({});
      })
    );

  }

  /**
   *
   * @param arg
   * @returns
   */
  private handleCommand_hostContextChanged(arg: IHostContextChangedArg): Observable<ICommandViewModel> {
    console.log({
      context: 'hostContextChanged', arg: arg
    });

    return of({}).pipe(
      tap(() => {

        const contentVm: IContentViewModel = {
          ...this.contentBs$.getValue(),

          errorText: (arg.status ?? 'ok') == 'ok' ? undefined : arg.status,

          resVm: !arg.resData ? undefined
            : { data: arg.resData },

          affiliateVm: !arg.affiliateData ? undefined
            : { data: arg.affiliateData },

          genericFieldsVm: !arg.fieldData ? undefined
            : {
              data: arg.fieldData,

              title: arg.fieldData.title,

              parts: arg.fieldData.fields.map(f => ({ title: f.title, value: f.value }))
            },

          resAuditVm: !arg.resAuditData ? undefined
            : {
              data: arg.resAuditData,

              errorMessage: arg.resAuditData.authorized == 'yes' ? '' : arg.resAuditData.authorized,

              records: arg.resAuditData.records.map((r) => ({
                data: r,

                title: `${r.dateEST} ${r.timeEST}`,
                subTitle: `${r.user} (${r.module})`,

                changes: r.changes.filter(c => c.fields && c.fields.length > 0).map(c => ({
                  data: c,

                  title: c.name,
                  fields: c.fields.map(f => ({
                    data: f,

                    title: f.field
                  }))
                }))
              }))
            },

          lastUpdated: DateUtil.now()
        };

        console.log({ context: 'hostContextChanged', data: arg });

        this.contentBs$.next(contentVm);
      })
    );
  }

  /**
   *
   * @param arg
   */
  private handleCommand_initialize(arg: IHostInitializationArg): Observable<ICommandViewModel> {
    console.log({
      context: 'initialize', data: { userId: arg?.loginResult?.userId, deploymentType: arg?.loginResult?.deploymentType }
    });

    return of({}).pipe(
      tap(() => {
        const cvm: IContentViewModel = {
          initializedVm: {
            data: { userId: arg.loginResult?.userId }
          },

          lastUpdated: DateUtil.now()
        };

        const user: IUser = {
          userName: arg.loginResult.userId,
          roleCodes: [...arg.loginResult.scUserGroups]
        };
        this.authService.loginProcess(user, arg.loginResult.accessToken, arg.loginResult.expiresIn);

        this.contentBs$.next(cvm);
      })
    );

  }

  /**
   *
   * @param action
   * @param paramData
   * @returns
   */
  private pageCommand<typeOfData extends ICommandResultData>(action: string, paramData: any): Observable<ICommandResult<typeOfData>> {
    return this.commonService.getData('PageCommand', 'app-buddy', action, paramData, '/api/AppBuddy').pipe(
      map((result: ICommandResult<typeOfData>) => {
        return {
          workingText: result.workingText,
          errorText: result.errorText,
          data: result.data
        };
      })
    );
  }

  /**
   *
   * @param data
   */
  copyToClipboard(data: string) {
    navigator.clipboard.writeText(data);
  }

  /**
   *
   * @param tab
   */
  processPickupZone(cvm: IContentViewModel) {
    this.actionBs$.next({
      type: 'pickup-zone-changed',
      contentVm: cvm,
      paramData: {
        pickupZone: cvm.affiliateVm.data.pickupZone
      }
    })
  }

}

/**
 *
 */
interface IAction {
  type: string;
  contentVm: IContentViewModel;
  paramData: any;
}

/**
 *
 */
interface IViewModel {
  params?: Params;
}

/**
 *
 */
interface ICommandResult<typeOfdata extends ICommandResultData> {
  workingText?: string;
  errorText?: string;

  data?: typeOfdata;
}

interface ICommandResultData {
  status: string;
}

interface ICommandResultAction extends ICommandResultData {
  resultData: any;
}

interface IAffiliateDispatchWorkflowItemData {
  priority: number;
  code: string;
  name: string;
  phone: string;
  email: string;
  resState: string;
}

/**
 *
 */
interface IHeartBeatViewModel {
  data?: IHostHeartBeatArg;
}

interface ICommandViewModel {
  workingText?: string;
  errorText?: string;
}

/**
 *
 */
interface IContentViewModel {

  errorText?: string;

  initializedVm?: {
    data: {
      userId: string;
    };
  };

  genericFieldsVm?: {
    data: IFieldData;

    title: string;

    parts: { title: string; value: string }[];
  };

  resVm?: {
    data: IResData;
  };

  affiliateVm?: {
    data: IAffiliateData;
  };

  resAuditVm?: {
    data: IResAuditData;

    errorMessage: string;

    records: {
      data: IResAuditDataRecord;

      title: string;
      subTitle: string;

      changes: {
        data: IResAuditDataRecordChange;

        title: string;

        fields: {
          data: IResAuditDataRecordChangeField;

          title: string;
        }[];
      }[];
    }[];
  }

  lastUpdated?: Date;
}

/**
 *
 */
interface ILoginResultData {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  userId: string;
  deploymentType: string;
  userIdInt: number;
  scUserGroups: [];
  scUserCustomerCodes: string;
  scUserParentGroupCodes: string;
}

/**
 *
 */
interface IResData {
  segmentRowId?: number;
  confirmationNumber: string;
  pickupDateData: {
    date: string;
    time: string;
  };
  totalPrice: number;
}

/**
 *
 */
interface IAffiliateData {
  selectedCode: string;
  pickupZone: string;
  dispatchWorkflowItems: IAffiliateDispatchWorkflowItemData[];
}

/**
 *
 */
interface IResAuditData {
  authorized: string;

  records: IResAuditDataRecord[];
}

/**
 *
 */
interface IResAuditDataRecord {
  dateEST: string;
  timeEST: string;
  description: string;
  module: string;
  user: string;
  action: string;

  changes: IResAuditDataRecordChange[];
}

/**
 *
 */
interface IResAuditDataRecordChange {
  name: string;
  fields: IResAuditDataRecordChangeField[];
}

/**
 *
 */
interface IResAuditDataRecordChangeField {
  field: string;
  before: string;
  after: string;
}

/**
 *
 */
interface IFieldData {
  title: string;

  fields: {
    title: string;
    value: string
  }[];
}

//#region host event arguments

/**
 *
 */
interface IHostHeartBeatArg {
  dateTime: Date;
}

/**
 *
 */
interface IHostContextChangedArg {
  status: string;

  resData?: IResData,

  affiliateData?: IAffiliateData;

  fieldData?: IFieldData;

  resAuditData?: IResAuditData;
}

/**
 *
 */
interface IHostInitializationArg {
  loginResult: ILoginResultData;
}

//#endregion event data

